import type { FC } from "react";
import { Fragment } from "react";

import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/20/solid";

import Image from "~/atoms/Image";
import Link from "~/atoms/Link";
import SkeletonLoader from "~/atoms/SkeletonLoader";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import { useSession } from "~/providers/SessionProvider";
import classNames from "~/util/classNames";
import { formatUsersName } from "~/util/formatting";

interface UserAvatarProps {
  avatar?: string;
  isLoading: boolean;
}

const UserAvatar: FC<UserAvatarProps> = ({ avatar, isLoading }) => {
  const profileImage = avatar ? `/app/assets/${avatar}` : null;

  if (isLoading) {
    return <SkeletonLoader width={32} height={32} circle />;
  }
  if (profileImage && !isLoading) {
    return (
      <Image
        className="h-8 w-8 rounded-full bg-gray-50"
        src={profileImage}
        alt="User avatar"
        width={32}
        height={32}
      />
    );
  }

  // Fall back to default profile icon.
  return <UserCircleIcon className="h-8 w-8 rounded-full text-gray-400" />;
};

interface NavigationItem {
  name: string;
  href?: string;
  onClick?: () => void;
}

interface PureProfileDropdownProps {
  navigation: NavigationItem[];
  isLoading: boolean;
  username?: string | null | undefined;
  email?: string;
  avatar?: string;
}

export const PureProfileDropdown: FC<PureProfileDropdownProps> = ({
  navigation,
  isLoading,
  username,
  email,
  avatar,
}) => (
  <Menu as="div" className="relative">
    <MenuButton className="-m-1.5 flex items-center p-1.5" id="avatar">
      <span className="sr-only">Open user menu</span>
      <UserAvatar avatar={avatar} isLoading={isLoading} />
      <span className="hidden lg:flex lg:items-center">
        <span
          className="ml-4 text-sm leading-6 font-semibold text-gray-900"
          aria-hidden="true"
        >
          {isLoading ? (
            <SkeletonLoader width={80} height={20} />
          ) : (
            username || "Account"
          )}
        </span>
        {!isLoading && (
          <ChevronDownIcon
            className="ml-2 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        )}
      </span>
    </MenuButton>
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <MenuItems className="absolute right-0 z-10 mt-2.5 max-w-xs min-w-[200px] origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
        <MenuItem>
          <div className="flex flex-col px-3 py-2 pb-3">
            <span className="text-sm font-bold text-gray-900">
              {username || "Account"}
            </span>
            <span className="text-sm text-gray-700">{email}</span>
          </div>
        </MenuItem>
        {navigation.map((item) => (
          <MenuItem key={item.name}>
            {({ focus }) => (
              <Link
                href={item?.href}
                onClick={item?.onClick}
                className={classNames(
                  focus ? "bg-gray-50" : "",
                  "block px-3 py-2 text-sm leading-6 text-gray-900"
                )}
              >
                {item.name}
              </Link>
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Transition>
  </Menu>
);

const ProfileDropdown = () => {
  const { data, isLoading } = useCurrentUser();
  const { signOut } = useSession();

  const role = data?.internalRole;

  const userNavigation = [
    ...(role === "landlord"
      ? [{ name: "Account Settings", href: "/settings/" }]
      : []),
    { name: "User Settings", href: "/settings/user-settings" },
    {
      name: "Sign out",
      onClick: () => signOut(),
    },
  ];

  return (
    <PureProfileDropdown
      navigation={userNavigation}
      isLoading={isLoading}
      username={!isLoading && data?.firstName ? formatUsersName(data) : null}
    />
  );
};

export default ProfileDropdown;
